import { CARD_ORGANIZATION_KEYS, OrganizationCodes } from '@revenuemarket/bank';
import QueryString from 'qs';

import { API } from '~apis/axios/instance';
import { BusinessEntity } from '~apis/business-entity';
import { FilterOperation, LabeledBy } from '~apis/cash-flow-overview/transactions/getTransactions';
import {
  CursorQueryRequestParams,
  CursorQueryResponse,
  Page,
  ResponseBaseWithData,
} from '~types/response';

export type CancelType = 'NONE' | 'PARTIAL_CANCEL' | 'FULL_CANCEL';

export type PaymentType = 'LUMP_SUM' | 'INSTALLMENT' | 'OTHER';

export type HomeForeignType = 'DOMESTIC' | 'FOREIGN';

/**
 * 통합 카드 내역 반영된 결과로 받을 것인지 말것인지
 * UNIFIED : 카드 이용 내역 반영 시 추가 필요
 */
export type CardProjectionType = 'NONE' | 'BILLING';

export interface CardApprovalRequest {
  startDate: string;
  endDate: string;
  cardNos?: string[];
  cancelTypes?: CancelType[];
  paymentTypes?: PaymentType[];
  homeForeignTypes?: HomeForeignType[];
  searchParam?: string;
  page?: number;
  size?: number;
  sort?: string[];
}

export interface CardApproval {
  approvalNo: string;
  organizationCode: OrganizationCodes;
  organizationName: string;
  companyId: string;
  cardNo: string;
  currency: string;
  usedAmount: number;
  usedKrwAmount: number;
  canceledAmount: number;
  canceledKrwAmount: number;
  cancelType: CancelType;
  homeForeignType: HomeForeignType;
  paymentType: PaymentType;
  vat: number;
  installmentMonth: number;
  memberStoreName: string;
  memberStoreType?: string;
  memberStoreCorpNo: string;
  usedAt: string;
  userNames: string[];
  aliasNames: string[];
  createdAt: string;
  updatedAt: string;
  id: number;
  memo?: string;
}

export const getCardApprovals = ({
  companyId,
  request,
}: {
  companyId: string;
  request: CardApprovalRequest;
}) => {
  const params = QueryString.stringify(request, { arrayFormat: 'repeat' });

  return API.get<
    ResponseBaseWithData<
      Page<CardApproval> & {
        totalUsedAmount: number;
        totalVatAmount: number;
      }
    >
  >(`/api/v3/companies/${companyId}/corporate-cards/approvals?${params}`);
};

export interface ApprovalMappedByPurchaseResponse {
  purchaseId: number;
  approval: CardApproval | null;
}

export const getApprovalMappedByPurchase = ({
  companyId,
  purchaseId,
}: {
  companyId: string;
  purchaseId: number;
}) => {
  return API.get<ResponseBaseWithData<ApprovalMappedByPurchaseResponse>>(
    `/api/v3/companies/${companyId}/corporate-cards/approvals/by-purchase?purchaseId=${purchaseId}`,
  );
};

export interface CardPurchaseRequest {
  startDate: string;
  endDate: string;
  cardNos?: string[];
  cancelTypes?: CancelType[];
  paymentTypes?: PaymentType[];
  homeForeignTypes?: HomeForeignType[];
  searchParam?: string;
  page?: number;
  size?: number;
  sort?: string[];
}

export interface CardPurchase {
  id: number;
  companyId: string;
  organizationCode: OrganizationCodes;
  organizationName: string;
  cardNo: string;
  approvalNo?: string;
  currency: string;
  usedAmount: number;
  usedKrwAmount: number;
  canceledAmount?: number;
  canceledKrwAmount?: number;
  cancelType: CancelType;
  fee?: number;
  vat?: number;
  cashBack?: number;
  paymentType: PaymentType;
  installmentMonth?: number;
  homeForeignType: HomeForeignType;
  memberStoreAddr?: string;
  memberStoreName: string;
  memberStoreNo?: string;
  memberStoreCorpNo?: string;
  memberStoreType?: string;
  memberStoreTelNo?: string;
  usedAt?: string;
  purchaseDate?: string;
  purchaseYearMonth?: string;
  paymentDueDate?: string;
  usedDateOrPurchaseDate: string;
  userNames: string[];
  aliasNames: string[];
  memo?: string;
}

export const getCardPurchases = ({
  companyId,
  request,
}: {
  companyId: string;
  request: CardPurchaseRequest;
}) => {
  const params = QueryString.stringify(request, { arrayFormat: 'repeat' });

  return API.get<
    ResponseBaseWithData<
      Page<CardPurchase> & {
        totalUsedAmount: number;
        totalVatAmount: number;
      }
    >
  >(`/api/v3/companies/${companyId}/corporate-cards/purchases?${params}`);
};

export interface PurchaseMappedByApprovalResponse {
  approvalId: number;
  purchase: CardPurchase | null;
}

export const getPurchaseMappedByApproval = ({
  companyId,
  approvalId,
}: {
  companyId: string;
  approvalId: number;
}) => {
  return API.get<ResponseBaseWithData<PurchaseMappedByApprovalResponse>>(
    `/api/v3/companies/${companyId}/corporate-cards/purchases/by-approval?approvalId=${approvalId}`,
  );
};

export enum CardTransactionType {
  APPROVAL,
  PURCHASE,
}

export type BillingType = 'REGULAR' | 'PRE_PAYMENT' | 'ADJUSTMENT';

export type RangeType = 'PAYMENT' | 'USED_DATE' | 'TRANSACTION_DATE';

export interface CardBillingRequest {
  startDate?: string;
  endDate?: string;
  billingId?: number;
  billingType?: BillingType;
  transactionId?: number;
  onlyUnclassified?: boolean;
  rangeType?: RangeType;
  searchParam?: string;
  page?: number;
  size?: number;
  sort?: string[];
  categoryLabelKeys?: string[];
  businessEntityIds?: number[];
  customLabelIds?: number[];
  excludePrePaidDuplicate?: boolean;
  organizationCodes?: OrganizationCodes[];
  businessEntityIdFilterOp?: FilterOperation;
  customLabelFilterOp?: FilterOperation;
  categoryLabelFilterOp?: FilterOperation;
  organizationCodeFilterOp?: FilterOperation;
  cardNos?: string[];
  cardNoFilterOp?: FilterOperation;
}

export interface BillingMapping {
  billingType: BillingType;
  billingItemId: number;
  transactionId: number;
  transactionDate: string;
  transactionAmount: number;
  mappedAmount: number;
}

export interface LabeledCardBillingTransaction {
  billingItemId: number;
  billingType: BillingType;
  cardNo?: string;
  userNames?: string;
  aliasNames: string[];
  memberStoreName: string;
  memberStoreType?: string;
  paymentAmount: number;
  usedAmount?: number;
  category?: string;
  categoryLabelKey?: string;
  companyDetailedCategory?: string;
  businessEntityId?: number;
  businessEntityLabel?: string;
  groupLabelId?: number | null;
  groupLabel?: string;
  mappings: BillingMapping[];
  labelId: number;
  memo?: string;
  memoId?: number;
  usedDate?: string;
  paymentDate?: string;
  billingId: number;
  billingKey: IBillingKey;
  organizationCode: OrganizationCodes;
  withdrawalDueDate: string;
  labeledBy?: LabeledBy;
}

interface IBillingKey {
  billingType: string;
  billingId: number;
}

export const getCardBillingTransactions = ({
  companyId,
  request,
}: {
  companyId: string;
  request: CardBillingRequest;
}) => {
  const params = QueryString.stringify(request, { arrayFormat: 'repeat' });

  return API.get<
    ResponseBaseWithData<
      Page<LabeledCardBillingTransaction> & {
        totalPayoutAmountSum: number;
      }
    >
  >(`/api/v2/companies/${companyId}/labelling/card-billing-item-labels?${params}`);
};

export const getCardBillingItem = ({
  companyId,
  itemId,
}: {
  companyId: string;
  itemId: number;
}) => {
  return API.get<ResponseBaseWithData<LabeledCardBillingTransaction>>(
    `/api/v2/companies/${companyId}/labelling/card-billing-item-labels/${itemId}`,
  );
};

export interface CardBillingLabelingRequest {
  labelingItems: ICardLabelingItemsItem[];
}

export interface ICardLabelingItemsItem {
  transactionId: number;
  transactionAmount: number;
  categoryId: number;
  businessEntityId: number | null;
  customLabelId: number | null;
  companyDetailedCategoryId: number | null;
  billingType: BillingType;
}

export const labelCardBillingItem = ({
  companyId,
  request,
}: {
  companyId: string;
  request: CardBillingLabelingRequest;
}) => {
  return API.put<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/card-billing-items/labeling`,
    {
      data: request,
    },
  );
};

export const labelBulkByAllRules = ({
  companyId,
  includeCustomLabels,
}: {
  companyId: string;
  includeCustomLabels: boolean;
}) => {
  return API.put(`/api/v3/companies/${companyId}/card-billing-items/labeling/bulk/by-all-rules`, {
    data: {
      includeCustomLabels,
    },
  });
};

export interface CardListResponse {
  maskedNumber: string;
  organizationCode: OrganizationCodes;
  holders: string[];
}

export const getCardList = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<{ items: CardListResponse[] }>>(
    `/api/v3/companies/${companyId}/card-labeling-rules/card-list`,
  );
};

export interface ScrapeSpecForCard {
  organizationCode: OrganizationCodes;
  organizationName: string;
  isConnected: boolean;
  maxInquiryPeriodInMonth: number;
  updatedAt: string;
}

export const getScrapeSpecForBillings = ({ companyId }: { companyId: string }) => {
  return API.get<
    ResponseBaseWithData<{
      items: ScrapeSpecForCard[];
    }>
  >(`/api/v3/companies/${companyId}/scrape-spec/corp-card-billings`);
};

export const getScrapeSpecForPrePayment = ({ companyId }: { companyId: string }) => {
  return API.get<
    ResponseBaseWithData<{
      items: ScrapeSpecForCard[];
    }>
  >(`/api/v3/companies/${companyId}/scrape-spec/corp-card-pre-payments`);
};

export interface CardBillingForMapping {
  id: number;
  billingType: BillingType;
  companyId: string;
  organizationCode: OrganizationCodes;
  paymentDate: string;
  withdrawalDueDate: string;
  paymentAmount: number;
  mappedAmount: number;
  paymentAccount: string;
  departmentCode: string;
  departmentName: string;
  cardNo: string;
  createdAt: string;
  updatedAt: string;
}

export const getBillingsForMapping = ({
  companyId,
  startDate,
  endDate,
  transactionId,
  forceAllOrganizations,
}: {
  companyId: string;
  startDate: string;
  endDate: string;
  transactionId: number;
  forceAllOrganizations?: boolean;
}) => {
  return API.get<
    ResponseBaseWithData<{
      organizationCode?: string | null;
      items: CardBillingForMapping[];
    }>
  >(`/api/v3/companies/${companyId}/corp-card-mappings/billings`, {
    params: {
      startDate,
      endDate,
      transactionId,
      forceAllOrganizations,
    },
  });
};

export interface BillingMapping {
  id: number;
  companyId: string;
  organizationCode: OrganizationCodes;
  cardNo: string;
  cardNames: string[];
  usedDate: string;
  paymentDate: string;
  billingType: BillingType;
  billingId: number;
  memberStoreName: string;
  memberStoreType: string;
  paymentAmount: number;
  mappedAmount: number;
  mappings: IMappingsTransactionForBilling[];
}

export interface IMappingsTransactionForBilling {
  billingType: BillingType;
  billingItemId: number;
  transactionId: number;
  transactionDate: string;
  transactionAmount: number;
  transactionDescription: string;
  transactionName: string;
  transactionType: string;
  mappedAmount: number;
}

export const getBillingMappingTransactions = ({
  companyId,
  billingItemId,
  billingType,
}: {
  companyId: string;
  billingItemId: number;
  billingType: BillingType;
}) => {
  return API.get<ResponseBaseWithData<BillingMapping>>(
    `/api/v3/companies/${companyId}/corp-card-mappings/billing-items/${billingItemId}?billingType=${billingType}`,
  );
};

export interface TransactionMappingBillings {
  id: number;
  companyId: string;
  transactionDescription: string;
  transactionName: string;
  transactionType: string;
  transactionDate: string;
  transactionAmount: number;
  mappings: IMappingsItem[];
  adjustments: IAdjustmentsItem[];
}

interface IMappingsItem {
  id: number;
  companyId: string;
  organizationCode: OrganizationCodes;
  cardNo: string;
  cardNames: string[];
  usedDate: string;
  paymentDate: string;
  billingType: BillingType;
  billingId: number;
  memberStoreName: string;
  memberStoreType: string;
  paymentAmount: number;
  mappedAmount: number;
}

interface IAdjustmentsItem {
  transactionId: number;
  amount: number;
  paymentDate: string;
  memo?: string;
  categoryId: number;
  category1: string;
  category2: string;
  category3: string;
  companyDetailedCategoryId?: number;
  companyDetailedCategory?: string;
  groupLabelId?: number;
  groupLabel?: string;
  businessEntityId?: number;
  businessEntityLabel?: string;
  categoryLabelKey: string;
}

export const getTransactionMappingBillings = ({
  companyId,
  transactionId,
}: {
  companyId: string;
  transactionId: number;
}) => {
  return API.get<ResponseBaseWithData<TransactionMappingBillings>>(
    `/api/v3/companies/${companyId}/transaction-label-mappings/${transactionId}/corp-card-billing-items`,
  );
};

export interface TransactionMappingBillingRequest {
  mappings: IMappingsRequestItem[];
  adjustments: IAdjustmentsRequestItem[];
}

interface IMappingsRequestItem {
  billingId: number;
  billingType: BillingType;
  billingItemId: number;
  mappingAmount: number;
}

interface IAdjustmentsRequestItem {
  categoryId: number;
  businessEntityId?: number;
  groupLabelId?: number;
  companyDetailedCategoryId?: number;
  amount: number;
  paymentDate: string;
  memo?: string;
}

export const updateTransactionMappingBillings = ({
  companyId,
  transactionId,
  request,
}: {
  companyId: string;
  transactionId: number;
  request: TransactionMappingBillingRequest;
}) => {
  return API.put<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/transaction-label-mappings/${transactionId}/corp-card-billing-items`,
    {
      data: request,
    },
  );
};

export const deleteMappingForBillingItem = ({
  companyId,
  billingItemId,
}: {
  companyId: string;
  billingItemId: number;
}) => {
  return API.delete<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/corp-card-mappings/billing-items/${billingItemId}`,
  );
};

export interface CardApprovalQueryParams extends CursorQueryRequestParams {
  cardNos?: string[];
  cancelTypes?: CancelType[];
  paymentTypes?: PaymentType[];
  searchParam?: string;
  cursorUsedAt?: string;
}

export const getCardApprovalByCursor = ({
  companyId,
  queryParams,
}: {
  companyId: string;
  queryParams: CardApprovalQueryParams;
}) => {
  const queryString = QueryString.stringify(queryParams, { arrayFormat: 'repeat' });

  return API.get<
    ResponseBaseWithData<
      CursorQueryResponse<CardApproval> & { nextCursor: { usedAt: string; id: number } | null }
    >
  >(`/api/v3/companies/${companyId}/corporate-cards/approvals/by-cursor?${queryString}`);
};

export const getCardApprovalById = ({
  companyId,
  approvalId,
}: {
  companyId: string;
  approvalId: number;
}) => {
  return API.get<ResponseBaseWithData<CardApproval>>(
    `/api/v3/companies/${companyId}/corporate-cards/approvals/${approvalId}`,
  );
};

export function patchChangeCardAlias({
  companyId,
  cardId,
  aliasName,
}: {
  companyId: string;
  cardId: number;
  aliasName: string;
}) {
  return API.patch(`/api/v2/companies/${companyId}/corporate-cards/${cardId}/alias-name`, {
    data: { aliasName },
  });
}

export type UnifiedCardTransactionRangeType = 'USED_OR_PURCHASE_DATE' | 'TRANSACTION_DATE';

export interface UnifiedCardTransactionRequest {
  page?: number;
  size?: number;
  sort?: string[];
  rangeType: UnifiedCardTransactionRangeType;
  startDate: string;
  endDate: string;
  organizationCodes?: OrganizationCodes[];
  organizationCodeFilterOp?: FilterOperation;
  cardNos?: string[];
  cancelTypes?: CancelType[];
  paymentTypes?: PaymentType[];
  homeForeignTypes?: HomeForeignType[];
  searchParam?: string;
  categoryLabelKeys?: string[];
  categoryLabelFilterOp?: FilterOperation;
  businessEntityIds?: number[];
  businessEntityIdFilterOp?: FilterOperation;
  groupLabelIds?: number[];
  groupLabelFilterOp?: FilterOperation;
}

export interface UnifiedCardTransaction {
  id: number;
  companyId: string;
  approvalNo?: string;
  organizationCode: OrganizationCodes;
  organizationName: string;
  cardNo: string;
  currency: string;
  usedAmount: number;
  usedKrwAmount: number;
  canceledAmount?: number;
  canceledKrwAmount?: number;
  cancelType: CancelType;
  homeForeignType: HomeForeignType;
  paymentType: PaymentType;
  fee?: number;
  vat?: number;
  cashBack?: number;
  installmentMonth?: number;
  memberStoreAddr?: string;
  memberStoreName: string;
  memberStoreNo?: string;
  memberStoreCorpNo?: string;
  memberStoreType?: string;
  memberStoreTelNo?: string;
  paymentDueDate?: string;
  usedOrPurchaseDate: string;
  usedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userNames: string[];
  aliasNames: string[];
  categoryLabelKey: string | null;
  memo?: string;
  categoryId?: number;
  category3?: string;
  companyDetailedCategoryId?: number;
  companyDetailedCategoryName?: string;
  businessEntityId?: number;
  businessEntityName?: string;
  groupLabelId?: number;
  groupLabelName?: string;
  labeledBy?: LabeledBy;
}

export const getUnifiedCardTransactions = ({
  companyId,
  request,
}: {
  companyId: string;
  request: UnifiedCardTransactionRequest;
}) => {
  return API.get<
    ResponseBaseWithData<
      Page<UnifiedCardTransaction> & {
        totalUsedAmount: number;
      }
    >
  >(`/api/v3/companies/${companyId}/unified-card-transaction/labels`, { params: request });
};

export interface UnifiedCardTransactionLabelingRequest {
  labels: UnifiedCardTransactionLabeling[];
}

export interface UnifiedCardTransactionLabeling {
  unifiedCardTransactionId: number;
  categoryId: number | null;
  companyDetailedCategoryId?: number;
  groupLabelId?: number;
  businessEntityId?: number;
}

export const labelUnifiedCardTransaction = ({
  companyId,
  request,
}: {
  companyId: string;
  request: UnifiedCardTransactionLabelingRequest;
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/unified-card-transaction/labels/bulk-upsert`,
    {
      data: request,
    },
    true,
  );
};

interface IExistingBusinessEntitiesItem {
  organizationKey: CARD_ORGANIZATION_KEYS;
  businessEntity: BusinessEntity;
}

export interface GetCardBusinessEntitiesResponse {
  existingBusinessEntities: IExistingBusinessEntitiesItem[];
  creatableCardOrganizations: Array<{
    organizationKey: CARD_ORGANIZATION_KEYS;
    aliasName: string;
    corporateName: string;
    businessRegistrationNumber: string;
  }>;
}

export const getCardBusinessEntities = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<GetCardBusinessEntitiesResponse>>(
    `/api/v2/companies/${companyId}/business-entities/cards`,
  );
};
